import { Coordinates } from '@max/set-page-ui';
import { GeolocationState } from '@uidotdev/usehooks';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

type Geolocation = Pick<GeolocationState, 'loading' | 'latitude' | 'longitude'>;

export const useCoordinates = ({
  useGps,
}: {
  useGps: boolean;
}): Coordinates | undefined => {
  const [geolocation, setGeolocation] = useState<Geolocation>({
    loading: true,
    latitude: null,
    longitude: null,
  });
  const iplocation = useQuery<Coordinates>(
    'coordinates',
    () => fetch('/geo').then((r) => r.json()),
    { enabled: !geolocation.loading && geolocation.latitude == null },
  );

  const fallbackToIpLocation = () =>
    setGeolocation((geo) => ({
      ...geo,
      loading: false,
    }));

  useEffect(() => {
    if (!useGps || !('geolocation' in navigator)) {
      fallbackToIpLocation();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeolocation({
          loading: false,
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        });
      },
      fallbackToIpLocation,
      // `timeout` is required: in-app browsers (e.g. Instagram on iOS) often
      // never surface the permission prompt, so without it neither callback
      // ever fires and `loading` stays true forever. On timeout we fall back
      // to IP-based location.
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }, [useGps]);

  return useMemo(() => {
    if (geolocation.loading) {
      return undefined;
    }

    if (geolocation.latitude && geolocation.longitude) {
      return {
        latitude: geolocation.latitude,
        longitude: geolocation.longitude,
      };
    }

    return iplocation.data;
  }, [
    geolocation.loading,
    geolocation.latitude,
    geolocation.longitude,
    iplocation.data,
  ]);
};
